import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { AlertTriangle, ExternalLink, Pencil } from 'lucide-react';
import api from '../../../lib/api';
import Button from '../../../components/Button';
import QuillEditor from '../../../components/QuillEditor';
import RichContent, { normalizeRichText } from '../../../components/RichContent';
import Chip from '../../../components/Chip';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import AdminModal from '../../components/AdminModal';
import AdminPageShell from '../../components/AdminPageShell';
import AdminTabs from '../../components/AdminTabs';
import AllFieldsSection from '../../components/AllFieldsSection';
import EntityAuditList from '../../components/EntityAuditList';
import FieldList from '../../components/FieldList';
import InitiativeSidesTab from './InitiativeSidesTab';
import InitiativeLifecycleCard from './InitiativeLifecycleCard';
import type { StatusEvent } from './InitiativeLifecycleCard';
import InitiativeProponentsCard from './InitiativeProponentsCard';
import InitiativeCitizenContentCard from './InitiativeCitizenContentCard';
import InitiativeBlueBookCard from './InitiativeBlueBookCard';
import InitiativeDocumentsCard from './InitiativeDocumentsCard';
import InitiativeFiscalImpactCard from './InitiativeFiscalImpactCard';
import OfficialSourceUpdates from '../../../components/ballot-initiatives/detail/OfficialSourceUpdates';
import type { BlueBookSection } from './InitiativeBlueBookCard';
import type { Proponent } from './InitiativeProponentsCard';
import { scopesForJurisdiction } from './InitiativesView';
import {
  MEASURE_TYPES,
  INITIATIVE_SOURCES,
  INITIATIVE_STATUSES,
  JURISDICTION_LEVELS,
  measureTypeLabel,
  initiativeSourceLabel,
  jurisdictionLabel,
  statusLabel,
  statusVariant,
  isBallotVisible,
  isPublished,
  isPubliclyVisible,
  publicationLabel,
  publicationTone,
  defaultThresholdFor,
  measurePassed,
  yesPercentage,
} from '../../../constants/ballotInitiatives';
import { HelpTrigger } from '../../../help';
import { formatDate } from '../../lib/format';
import { initiativeDisplayName, initiativeNumberLine, measureDocumentLinks } from '../../../components/ballot-initiatives/initiativeDisplay';
import { measureBlueBookUrl } from '../../../components/ballot-initiatives/blueBook';
import type { ElectionCycle } from './CyclesView';
import type { EndorsementRow } from '../../../constants/endorsements';

export interface InitiativeCommittee {
  id: string;
  committee_name: string;
  committee_type: string;
  contact_person_id: string | null;
  contact_person_name: string | null;
  contact_person_email: string | null;
  organization_id: string | null;
  organization_name: string | null;
  website_url: string | null;
  finance_committee_id: string | null;
  finance_committee_name: string | null;
  /** The linked committee's TRACER id (co_id). Absent = a legacy "Not matched" row. */
  finance_co_id?: string | null;
  /** Notes about this committee on this measure — the only committee text the link owns. */
  notes: string | null;
  /** From the committee's record (Campaign Committee Registry PRD §5.5). */
  purpose?: string | null;
  matched?: boolean;
}

/** Another measure a committee is linked to (Shared Committee Finance PRD §5.3, §5.5). */
export interface LinkedMeasure {
  id: string;
  number: string | null;
  name: string | null;
  status: string;
  side: string;
  /** Published, ballot-visible and on the same election, so the link makes the committee shared. */
  qualifies: boolean;
}

export interface SharedCommittee {
  co_id: string;
  committee_name: string;
  purpose: string | null;
  both_sides: boolean;
  total_raised: number;
  total_spent: number;
  also_backs: LinkedMeasure[];
}

export interface InitiativeArgument {
  id: string;
  side: string;
  source_type: string;
  title: string | null;
  body: string;
  source_name: string | null;
  source_url: string | null;
}

/** The shared endorsement row (ENDORSEMENTS_PRD §5.1) plus the measure's side. */
export interface InitiativeEndorsement extends EndorsementRow {
  side: string;
}

export interface SideFinanceDetail {
  top_donors: Array<{ name: string; amount: number; gifts: number }>;
  recent_expenditures: Array<{ date: string; payee: string; amount: number; purpose: string | null }>;
  has_finance_link: boolean;
}

export interface SideFinance {
  committee_count: number;
  linked_committee_count: number;
  /** Totals count these only (Shared Committee Finance PRD §5.2). */
  dedicated_committee_count?: number;
  shared_committee_count?: number;
  total_raised: number;
  total_spent: number;
}

export interface InitiativeDetail {
  id: string;
  measure_number: string | null;
  ballot_title: string | null;
  popular_name: string | null;
  description: string | null;
  full_text_url: string | null;
  measure_type: string;
  initiative_source: string;
  subject_area: string | null;
  election_calendar_id: string;
  election_calendar_name: string | null;
  election_calendar: {
    id: string;
    name: string | null;
    election_year: number;
    election_type: string | null;
    blue_book_url?: string | null;
  } | null;
  jurisdiction_level: string;
  jurisdiction_name: string | null;
  electoral_district_id: string | null;
  district_display: string | null;
  status: string;
  publication_status: string;
  published_at: string | null;
  published_by: string | null;
  published_by_name: string | null;
  /** The on-ballot record this (non-visible) measure duplicates, if one exists (PRD v2 §5.7). */
  ballot_visible_twin: { id: string; label: string; status: string } | null;
  signatures_required: number | null;
  signatures_submitted: number | null;
  signatures_valid: number | null;
  petition_deadline: string | null;
  votes_yes: number | null;
  votes_no: number | null;
  result_certified: boolean;
  effective_date: string | null;
  fiscal_impact_url: string | null;
  blue_book_url: string | null;
  ballotpedia_url: string | null;
  notes: string | null;
  sos_measure_id: string | null;
  plain_language_summary: string | null;
  what_yes_means: string | null;
  what_no_means: string | null;
  background_context: string | null;
  plain_language_long_description: string | null;
  who_supports_and_why: string | null;
  who_opposes_and_why: string | null;
  approval_threshold_pct: number | string | null;
  sos_initiative_number: string | null;
  unofficial_caption: string | null;
  ballot_order: number | null;
  is_single_subject_approved: boolean | null;
  full_text: string | null;
  fiscal_impact_detail: Record<string, string> | null;
  content_provenance: Record<string, any> | null;
  blue_book_edition: string | null;
  blue_book_retrieved_on: string | null;
  blue_book_entry_status: string;
  blue_book_verified_at: string | null;
  blue_book_sections: BlueBookSection[];
  fiscal_impact_statement: string | null;
  created_at: string;
  updated_at: string;
  committees: InitiativeCommittee[];
  milestones: Array<{ type: string; date: string; label: string }>;
  tracking: Array<{ id: string; workspace_name: string; position: string | null; is_hot: boolean }>;
  arguments: InitiativeArgument[];
  endorsements: InitiativeEndorsement[];
  status_history: StatusEvent[];
  proponents: Proponent[];
  finance_by_side: {
    support: SideFinance;
    oppose: SideFinance;
  } | null;
  side_finance_detail: {
    support: SideFinanceDetail;
    oppose: SideFinanceDetail;
  } | null;
  shared_committees_by_side?: {
    support: SharedCommittee[];
    oppose: SharedCommittee[];
  } | null;
  source_observations?: BallotSourceObservation[];
  source_documents?: BallotSourceDocument[];
}

interface AnalysisDraftRound {
  round: string;
  version: string | null;
  mailing_date_exact: string | null;
  mailing_date: string | null;
  comments_due_exact: string | null;
  comments_due: string | null;
  document_label: string | null;
  document_url: string | null;
}

interface BallotAnalysisFields {
  analysis_short_title?: string | null;
  analysis_status_exact?: string | null;
  research_staff_text?: string | null;
  fiscal_staff_text?: string | null;
  research_staff_email?: string | null;
  ballot_number_exact?: string | null;
  election_year_exact?: string | null;
  ballot_type_exact?: string | null;
  detail_page_url?: string | null;
  detail_url?: string | null;
  material_state?: string | Record<string, unknown> | null;
  draft_rounds?: AnalysisDraftRound[];
  selected_url?: string | null;
  stable_source_url?: string | null;
  delegated_url_sanitized?: string | null;
  draft_round?: string | null;
  draft_version?: string | null;
  page_count?: number | null;
  mapped_fields?: string[] | null;
  mailing_date_exact?: string | null;
  mailing_date?: string | null;
  comments_due_exact?: string | null;
  comments_due?: string | null;
}

interface BallotSourceObservation {
  resource_type?: string | null;
  detail_url?: string | null;
  raw_fields?: BallotAnalysisFields | null;
  parse_state?: string | null;
  parse_warnings?: unknown[] | null;
}

interface BallotSourceDocument {
  document_type?: string | null;
  exact_label?: string | null;
  acquisition_state?: string | null;
  extraction_state?: string | null;
  failure_message?: string | null;
  extraction_warnings?: unknown[] | null;
  final_url?: string | null;
  extracted_fields?: BallotAnalysisFields | null;
}

const ANALYSIS_RESOURCE_TYPES = new Set([
  'legislative_ballot_analysis_index',
  'legislative_ballot_analysis_page',
]);
const ANALYSIS_DOCUMENT_TYPES = new Set([
  'legislative_ballot_analysis_index',
  'legislative_ballot_analysis_page',
  'legislative_ballot_analysis_draft',
]);

export const ballotAnalysisEvidence = (initiative: Pick<InitiativeDetail, 'source_observations' | 'source_documents'>) => {
  const observations = (initiative.source_observations ?? []).filter(
    observation => ANALYSIS_RESOURCE_TYPES.has(observation.resource_type ?? '')
  );
  const documents = (initiative.source_documents ?? []).filter(
    document => ANALYSIS_DOCUMENT_TYPES.has(document.document_type ?? '')
  );
  const pageObservation = observations.find(
    observation => observation.resource_type === 'legislative_ballot_analysis_page'
  );
  const pageDocument = documents.find(
    document => document.document_type === 'legislative_ballot_analysis_page'
  );
  const fields = pageObservation?.raw_fields
    ?? pageDocument?.extracted_fields
    ?? observations[0]?.raw_fields
    ?? null;
  return { observations, documents, fields, pageObservation };
};

const evidenceWarningText = (warning: unknown): string => {
  if (typeof warning === 'string') return warning;
  if (!warning || typeof warning !== 'object') return 'Unspecified warning';
  const value = warning as Record<string, unknown>;
  return String(value.message ?? value.code ?? 'Unspecified warning');
};

export const BallotAnalysisCard: React.FC<{ initiative: InitiativeDetail }> = ({ initiative }) => {
  const { observations, documents, fields, pageObservation } = ballotAnalysisEvidence(initiative);
  if (observations.length === 0 && documents.length === 0) return null;
  const rounds = Array.isArray(fields?.draft_rounds) ? fields.draft_rounds : [];
  const detailUrl = fields?.detail_page_url || pageObservation?.detail_url || null;
  const warnings = [
    ...(pageObservation?.parse_warnings ?? []),
    ...documents.flatMap(item => item.extraction_warnings ?? []),
  ];
  return (
    <section className="card p-5" aria-labelledby="ballot-analysis-heading">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 id="ballot-analysis-heading" className="text-base font-semibold text-text-primary dark:text-text-dark-primary">Ballot Analysis &amp; Deadlines</h3>
          <p className="mt-1 text-xs text-text-secondary dark:text-text-dark-secondary">Administrator-only Legislative Council acquisition evidence. Draft and preliminary material is not Blue Book or public content.</p>
        </div>
        <Chip label="Draft / preliminary" variant="warning" size="sm" />
      </div>
      <FieldList fields={[
        { label: 'Analysis short title', value: fields?.analysis_short_title ?? '—' },
        { label: 'Exact analysis status', value: fields?.analysis_status_exact ?? '—' },
        { label: 'Ballot identity', value: [fields?.ballot_number_exact, fields?.election_year_exact, fields?.ballot_type_exact].filter(Boolean).join(' · ') || '—' },
        { label: 'Research staff', value: fields?.research_staff_text ?? '—' },
        { label: 'Fiscal staff', value: fields?.fiscal_staff_text ?? '—' },
        { label: 'Research staff email', value: fields?.research_staff_email ? <a className="text-primary-600 hover:underline dark:text-primary-400" href={`mailto:${fields.research_staff_email}`}>{fields.research_staff_email}</a> : '—' },
        { label: 'Analysis detail page', value: detailUrl ? <a className="inline-flex items-center gap-1 text-primary-600 hover:underline dark:text-primary-400" href={detailUrl} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-3 w-3" />Open exact source</a> : '—' },
      ]} />
      {rounds.length > 0 && <div className="mt-4"><h4 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary">Draft rounds and deadlines</h4><ul className="mt-2 space-y-2">{rounds.map((round, index) => <li key={`${round.round}-${round.version ?? index}`} className="rounded-lg border border-border-light p-3 text-sm dark:border-border-dark"><div className="flex flex-wrap items-center justify-between gap-2"><span className="font-medium">{round.document_label || `${round.round === 'first' ? 'First' : 'Later'} draft${round.version ? ` · ${round.version}` : ''}`}</span><Chip label="Draft" variant="warning" size="xs" /></div><p className="mt-1 text-text-secondary dark:text-text-dark-secondary">Mailing: {round.mailing_date_exact || (round.mailing_date ? formatDate(round.mailing_date) : 'Not recorded')} · Comments due: {round.comments_due_exact || (round.comments_due ? formatDate(round.comments_due) : 'Not recorded')}</p>{round.document_url && <a className="mt-2 inline-flex items-center gap-1 text-primary-600 hover:underline dark:text-primary-400" href={round.document_url} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-3 w-3" />Open draft source</a>}</li>)}</ul></div>}
      <div className="mt-4"><h4 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary">Acquisition custody</h4><ul className="mt-2 space-y-2">{documents.map((document, index) => { const extracted = document.extracted_fields; const sourceUrl = extracted?.selected_url || extracted?.stable_source_url || extracted?.delegated_url_sanitized || document.final_url; return <li key={`${document.document_type}-${index}`} className="rounded-lg bg-background-secondary p-3 text-sm dark:bg-background-dark-tertiary"><div className="flex flex-wrap items-center gap-2"><span className="font-medium">{document.exact_label || document.document_type?.replace(/_/g, ' ') || 'Analysis document'}</span><Chip label={`Acquisition: ${document.acquisition_state || 'unknown'}`} variant={document.acquisition_state === 'failed' ? 'danger' : 'neutral'} size="xs" /><Chip label={`Extraction: ${document.extraction_state || 'unknown'}`} variant={document.extraction_state === 'failed' ? 'danger' : 'neutral'} size="xs" /></div>{(extracted?.draft_round || extracted?.draft_version) && <p className="mt-1 text-text-secondary dark:text-text-dark-secondary">Draft: {[extracted.draft_round, extracted.draft_version].filter(Boolean).join(' · ')}</p>}{(extracted?.mailing_date_exact || extracted?.comments_due_exact) && <p className="mt-1 text-text-secondary dark:text-text-dark-secondary">Mailing: {extracted.mailing_date_exact || (extracted.mailing_date ? formatDate(extracted.mailing_date) : 'Not recorded')} · Comments due: {extracted.comments_due_exact || (extracted.comments_due ? formatDate(extracted.comments_due) : 'Not recorded')}</p>}{document.failure_message && <p className="mt-2 text-error-700 dark:text-error-300">Failure: {document.failure_message}</p>}{extracted?.page_count != null && <p className="mt-1 text-text-secondary dark:text-text-dark-secondary">Pages: {extracted.page_count}</p>}{sourceUrl && <a className="mt-2 inline-flex items-center gap-1 text-primary-600 hover:underline dark:text-primary-400" href={sourceUrl} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-3 w-3" />Open retained source</a>}</li>; })}</ul></div>
      {warnings.length > 0 && <div className="mt-4 rounded-lg border border-warning-300 p-3" role="status"><h4 className="text-sm font-semibold">Warnings</h4><ul className="mt-1 list-disc space-y-1 pl-5 text-sm">{warnings.map((warning, index) => <li key={index}>{evidenceWarningText(warning)}</li>)}</ul></div>}
    </section>
  );
};

/**
 * Grouped the way the work arrives rather than the way the schema is shaped:
 * Path to the Ballot is what you use in spring while a measure circulates,
 * Citizen Content is what you use in September when the Blue Book lands.
 */
const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'lifecycle', label: 'Path to the Ballot' },
  { key: 'content', label: 'Citizen Content' },
  { key: 'sides', label: 'Sides & Committees' },
  { key: 'source-changes', label: 'Source Changes' },
  { key: 'activity', label: 'Activity' },
];

export const inputClasses =
  'w-full rounded-lg border border-border-light bg-white px-3 py-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary';
export const labelClasses = 'mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary';

export const apiError = (error: any, fallback: string) =>
  error?.response?.data?.error ?? error?.response?.data?.message ?? fallback;

/**
 * Invalidate everything a mutation on a measure can leave stale: the detail
 * query, the initiatives list, and the Activity tab's audit feed. Child cards
 * must call this rather than spelling the detail key themselves — the key
 * lives here, and a card guessing it wrong leaves the screen stale until a
 * manual refresh.
 */
export const invalidateInitiativeQueries = (queryClient: QueryClient, initiativeId: string | undefined) => {
  queryClient.invalidateQueries({ queryKey: ['admin-elections', 'initiative', initiativeId] });
  queryClient.invalidateQueries({ queryKey: ['admin-list', 'elections-initiatives'] });
  queryClient.invalidateQueries({ queryKey: ['admin-accounts', 'audit', 'ballot_initiative', initiativeId ?? ''] });
  // The Operations header's "Needs publish" tile counts this measure.
  queryClient.invalidateQueries({ queryKey: ['ballot-initiative-operations-summary'] });
};

type EditFieldType = 'text' | 'textarea' | 'richtext' | 'number' | 'date' | 'checkbox';

interface EditField {
  key: string;
  label: string;
  type: EditFieldType;
  rows?: number;
  placeholder?: string;
}

/** Per-section edit forms (no global edit flag — each card edits only itself). */
const SECTION_FORMS: Record<string, { title: string; fields: EditField[] }> = {
  identity: {
    title: 'Edit Initiative Details',
    fields: [
      { key: 'measure_number', label: 'Measure Number', type: 'text', placeholder: 'e.g. Amendment A' },
      { key: 'popular_name', label: 'Popular Name', type: 'text', placeholder: 'Short public name' },
      { key: 'ballot_title', label: 'Ballot Title', type: 'textarea', rows: 3 },
      { key: 'description', label: 'SOS Full Title', type: 'richtext' },
      { key: 'subject_area', label: 'Subject Area', type: 'text', placeholder: 'e.g. taxation, education, healthcare' },
    ],
  },
  petition: {
    title: 'Edit Petition Tracking',
    fields: [
      { key: 'signatures_required', label: 'Signatures Required', type: 'number' },
      { key: 'signatures_submitted', label: 'Signatures Submitted', type: 'number' },
      { key: 'signatures_valid', label: 'Signatures Valid (verified)', type: 'number' },
      { key: 'petition_deadline', label: 'Petition Deadline', type: 'date' },
    ],
  },
  results: {
    title: 'Edit Election Results',
    fields: [
      { key: 'votes_yes', label: 'Votes Yes', type: 'number' },
      { key: 'votes_no', label: 'Votes No', type: 'number' },
      { key: 'result_certified', label: 'Result Certified', type: 'checkbox' },
      { key: 'effective_date', label: 'Effective Date', type: 'date' },
    ],
  },
  full_text: {
    title: 'Edit Final Measure Text',
    fields: [
      {
        key: 'full_text', label: 'Final Measure Text', type: 'textarea', rows: 14,
        placeholder: 'The measure text as filed — stored here as well as linked, so it survives the source URL moving.',
      },
    ],
  },
  links: {
    title: 'Edit Links & Resources',
    fields: [
      { key: 'full_text_url', label: 'Full Text URL', type: 'text' },
      { key: 'fiscal_impact_url', label: 'State Fiscal Impact Statement URL', type: 'text' },
      {
        key: 'blue_book_url',
        label: 'Blue Book URL',
        type: 'text',
        placeholder: "Blank uses the election cycle's Blue Book — set one here only to link this measure's own page",
      },
      { key: 'ballotpedia_url', label: 'Ballotpedia URL', type: 'text' },
    ],
  },
  notes: {
    title: 'Edit Admin Notes',
    fields: [
      { key: 'notes', label: 'Notes', type: 'richtext', placeholder: 'Internal notes about this initiative' },
    ],
  },
};

type EditFormState = Record<string, string | boolean>;

const seedSectionForm = (section: string, initiative: InitiativeDetail): EditFormState => {
  const form: EditFormState = {};
  for (const field of SECTION_FORMS[section].fields) {
    const raw = (initiative as unknown as Record<string, unknown>)[field.key];
    if (field.type === 'checkbox') form[field.key] = Boolean(raw);
    else if (field.type === 'date') form[field.key] = raw ? String(raw).slice(0, 10) : '';
    else form[field.key] = raw === null || raw === undefined ? '' : String(raw);
  }
  return form;
};

const CardHeader: React.FC<{ title: string; onEdit?: () => void }> = ({ title, onEdit }) => (
  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
    <h4 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary">{title}</h4>
    {onEdit && (
      <Button variant="ghost" size="sm" icon={Pencil} ariaLabel={`Edit ${title}`} onClick={onEdit}>
        Edit
      </Button>
    )}
  </div>
);

/**
 * A measure has no year of its own — the election cycle is its only time
 * relationship — so every "when" on this page is read from the cycle.
 */
const cycleLabel = (initiative: InitiativeDetail): string =>
  initiative.election_calendar_name
  || (initiative.election_calendar
    ? `${initiative.election_calendar.election_year} ${initiative.election_calendar.election_type ?? ''}`.trim()
    : '');

/**
 * Ballot initiative detail — the measure's identity, cycle, petition/result
 * state, and its two sides (committees, arguments, endorsements), rebuilt on
 * the People-detail pattern: per-section editing, confirmations on every
 * delete, and an audit Activity tab (ELECTIONS_TOOL_PLAN.md E4).
 */
const InitiativeDetailPage: React.FC = () => {
  const { initiativeId } = useParams<{ initiativeId: string }>();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('overview');
  const [editSection, setEditSection] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<EditFormState>({});
  const [classificationOpen, setClassificationOpen] = useState(false);
  const [classificationForm, setClassificationForm] = useState({
    measure_type: '',
    initiative_source: '',
    status: '',
    election_calendar_id: '',
    approval_threshold_pct: '',
    sos_initiative_number: '',
    unofficial_caption: '',
    ballot_order: '',
    is_single_subject_approved: '',
    jurisdiction_level: 'statewide',
    jurisdiction_name: '',
    electoral_district_id: '',
  });
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [unpublishOpen, setUnpublishOpen] = useState(false);

  const detail = useQuery({
    queryKey: ['admin-elections', 'initiative', initiativeId],
    queryFn: async () => {
      const response = await api.get(`/admin/ballot-initiatives/${initiativeId}`);
      return response.data.data as InitiativeDetail;
    },
    enabled: !!initiativeId,
  });

  const cycles = useQuery({
    queryKey: ['admin-elections', 'cycles'],
    queryFn: async () => {
      const response = await api.get('/election-calendars');
      return (response.data.data ?? []) as ElectionCycle[];
    },
    staleTime: 5 * 60 * 1000,
  });

  const districts = useQuery({
    queryKey: ['admin-elections', 'electoral-districts', 'CO'],
    queryFn: async () => {
      const response = await api.get('/reference/electoral-districts', { params: { state_code: 'CO' } });
      return (response.data.data?.districts ?? []) as Array<{ id: string; scope: string; display_label: string }>;
    },
    staleTime: 5 * 60 * 1000,
    enabled: classificationOpen,
  });

  const invalidate = () => invalidateInitiativeQueries(queryClient, initiativeId);

  const updateMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => api.put(`/admin/ballot-initiatives/${initiativeId}`, payload),
    onSuccess: () => {
      toast.success('Initiative updated');
      setEditSection(null);
      setClassificationOpen(false);
      invalidate();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to update initiative')),
  });

  const withdrawMutation = useMutation({
    mutationFn: () => api.put(`/admin/ballot-initiatives/${initiativeId}/deactivate`),
    onSuccess: () => {
      toast.success('Initiative withdrawn');
      setWithdrawOpen(false);
      invalidate();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to withdraw initiative')),
  });

  // Publish / unpublish — the editorial release act (PRD v2 §5.3). Same shape as
  // withdraw: one PUT, a toast, and the shared invalidation so the list, the
  // detail and the Activity tab all refresh.
  const publishMutation = useMutation({
    mutationFn: () => api.put(`/admin/ballot-initiatives/${initiativeId}/publish`),
    onSuccess: () => {
      toast.success('Initiative published');
      setPublishOpen(false);
      invalidate();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to publish initiative')),
  });
  const unpublishMutation = useMutation({
    mutationFn: () => api.put(`/admin/ballot-initiatives/${initiativeId}/unpublish`),
    onSuccess: () => {
      toast.success('Initiative unpublished');
      setUnpublishOpen(false);
      invalidate();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to unpublish initiative')),
  });

  if (detail.isLoading) {
    return (
      <AdminPageShell title="Ballot Initiative" backUrl="/admin-next/elections?tab=initiatives">
        <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">Loading initiative…</p>
      </AdminPageShell>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <AdminPageShell title="Ballot Initiative" backUrl="/admin-next/elections?tab=initiatives">
        <div className="card flex items-center gap-3 border-l-4 border-error-500 p-4">
          <AlertTriangle className="h-5 w-5 shrink-0 text-error-600 dark:text-error-400" />
          <p className="flex-1 text-sm text-text-secondary dark:text-text-dark-secondary">
            Couldn't load this ballot initiative.
          </p>
          <Button variant="outline" size="sm" onClick={() => detail.refetch()}>Retry</Button>
        </div>
      </AdminPageShell>
    );
  }

  const initiative = detail.data;

  const openSection = (section: string) => {
    setEditForm(seedSectionForm(section, initiative));
    setEditSection(section);
  };

  const openClassification = () => {
    setClassificationForm({
      measure_type: initiative.measure_type,
      initiative_source: initiative.initiative_source,
      status: initiative.status,
      election_calendar_id: initiative.election_calendar_id,
      jurisdiction_level: initiative.jurisdiction_level,
      jurisdiction_name: initiative.jurisdiction_name ?? '',
      electoral_district_id: initiative.electoral_district_id ?? '',
      approval_threshold_pct: String(initiative.approval_threshold_pct ?? defaultThresholdFor(initiative.measure_type)),
      sos_initiative_number: initiative.sos_initiative_number ?? '',
      unofficial_caption: initiative.unofficial_caption ?? '',
      ballot_order: initiative.ballot_order?.toString() ?? '',
      // '' is 'not determined' rather than 'no' — the tri-state survives the round trip.
      is_single_subject_approved: initiative.is_single_subject_approved == null
        ? ''
        : String(initiative.is_single_subject_approved),
    });
    setClassificationOpen(true);
  };

  const saveSection = () => {
    if (!editSection) return;
    const payload: Record<string, unknown> = {};
    for (const field of SECTION_FORMS[editSection].fields) {
      const value = editForm[field.key];
      if (field.type === 'richtext') {
        payload[field.key] = normalizeRichText(typeof value === 'string' ? value : '') || null;
      } else {
        payload[field.key] = typeof value === 'string' && value.trim() === '' ? null : value;
      }
    }
    updateMutation.mutate(payload);
  };

  const saveClassification = () => {
    updateMutation.mutate({
      measure_type: classificationForm.measure_type,
      initiative_source: classificationForm.initiative_source,
      status: classificationForm.status,
      election_calendar_id: classificationForm.election_calendar_id || initiative.election_calendar_id,
      jurisdiction_level: classificationForm.jurisdiction_level,
      jurisdiction_name: classificationForm.jurisdiction_name || null,
      electoral_district_id: classificationForm.electoral_district_id || null,
      approval_threshold_pct: classificationForm.approval_threshold_pct || null,
      sos_initiative_number: classificationForm.sos_initiative_number.trim() || null,
      unofficial_caption: classificationForm.unofficial_caption.trim() || null,
      ballot_order: classificationForm.ballot_order === '' ? null : classificationForm.ballot_order,
      is_single_subject_approved: classificationForm.is_single_subject_approved,
    });
  };

  const activeScopes = scopesForJurisdiction(classificationForm.jurisdiction_level);
  const scopedDistricts = activeScopes
    ? (districts.data ?? []).filter((district) => activeScopes.includes(district.scope))
    : [];
  const showDistrictSelect = activeScopes !== null && scopedDistricts.length > 0;
  const showJurisdictionName = classificationForm.jurisdiction_level !== 'statewide' && !showDistrictSelect;

  // Local measures skip the Title Board, so the lifecycle stays empty for them (O7).
  // Scored against the measure's own threshold: a bare yes > no reading is wrong for
  // any constitutional amendment, which needs 55% under Amendment 71 (2016).
  const thresholdPct = Number(initiative.approval_threshold_pct ?? 50);
  const yesPct = yesPercentage(initiative.votes_yes, initiative.votes_no);
  const passed = measurePassed(initiative.votes_yes, initiative.votes_no, thresholdPct);

  const showLifecycleCard = initiative.jurisdiction_level === 'statewide';

  const showPetitionCard =
    initiative.initiative_source === 'citizen' ||
    initiative.signatures_required !== null ||
    initiative.signatures_submitted !== null ||
    initiative.petition_deadline !== null;

  const published = isPublished(initiative.publication_status);

  // Labels and order live in the shared helper — the rail used to keep its own
  // drifted copy (v2 PRD §5.6.6).
  const links = measureDocumentLinks(initiative);

  return (
    <AdminPageShell
      title={initiativeDisplayName(initiative)}
      description={
        [cycleLabel(initiative), initiativeNumberLine(initiative), statusLabel(initiative.status)]
          .filter(Boolean)
          .join(' · ')
      }
      backUrl="/admin-next/elections?tab=initiatives"
      showHeading={false}
      actions={
        // One group, emphasis left → right; one primary. Publish is never gated by
        // lifecycle status (a published pre-ballot measure appears automatically
        // once its status qualifies — PRD v2 §5.2.12).
        <div className="flex items-center gap-2" role="group" aria-label="Initiative actions">
          <HelpTrigger contentKey="initiative-publication" size="sm" ariaLabel="Help for publishing" />
          <Button
            variant="outline"
            size="sm"
            disabled={initiative.status === 'withdrawn'}
            onClick={() => setWithdrawOpen(true)}
          >
            Withdraw
          </Button>
          {published ? (
            <Button variant="secondary" size="sm" onClick={() => setUnpublishOpen(true)} disabled={unpublishMutation.isPending}>
              Unpublish
            </Button>
          ) : (
            <Button variant="primary" size="sm" onClick={() => setPublishOpen(true)} disabled={publishMutation.isPending}>
              Publish
            </Button>
          )}
        </div>
      }
    >
      <div className="flex flex-wrap items-center gap-2 text-sm text-text-secondary dark:text-text-dark-secondary" aria-label="Publication state">
        <Chip label={publicationLabel(initiative.publication_status)} tone={publicationTone(initiative.publication_status)} size="sm" />
        {published && (
          <span>
            {initiative.published_at
              ? `Published ${formatDate(initiative.published_at)}${initiative.published_by_name ? ` by ${initiative.published_by_name}` : ''}`
              : 'Published (backfilled)'}
          </span>
        )}
        {!isPubliclyVisible(initiative) && (
          <span>
            {published
              ? 'Published, but not visible to citizens until its status is qualified or on the ballot.'
              : isBallotVisible(initiative.status)
                ? 'On the ballot by status, but not visible to citizens until published.'
                : 'Not visible to citizens: not published, and not yet on the ballot.'}
          </span>
        )}
      </div>
      <AdminTabs
        tabs={TABS}
        activeKey={tab}
        onChange={setTab}
        ariaLabel="Ballot initiative sections"
      />

      {tab === 'overview' && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-2">
            <div className="card p-5">
              <CardHeader title="Initiative Details" onEdit={() => openSection('identity')} />
              {initiative.ballot_title && initiative.ballot_title !== initiativeDisplayName(initiative) && (
                <details className="mb-4 rounded-lg border border-border-light p-3 dark:border-border-dark">
                  <summary className="cursor-pointer text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
                    Official ballot title
                  </summary>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-text-primary dark:text-text-dark-primary">
                    {initiative.ballot_title}
                  </p>
                </details>
              )}
              <FieldList
                fields={[
                  { label: 'Measure Number', value: initiative.measure_number ?? '—' },
                  { label: 'Popular Name', value: initiative.popular_name ?? '—' },
                  {
                    label: 'SOS Full Title',
                    value: initiative.description ? (
                      <RichContent
                        html={initiative.description}
                        className="text-sm text-text-primary dark:text-text-dark-primary"
                      />
                    ) : '—',
                    fullWidth: true,
                    wrap: 'words',
                  },
                  { label: 'Subject Area', value: initiative.subject_area ?? '—' },
                ]}
              />
            </div>

            <div className="card p-5">
              <CardHeader title="Classification" onEdit={openClassification} />
              <FieldList
                fields={[
                  { label: 'Measure Type', value: measureTypeLabel(initiative.measure_type) },
                  { label: 'Source', value: initiativeSourceLabel(initiative.initiative_source) },
                  {
                    label: 'Status',
                    value: (
                      <Chip
                        label={statusLabel(initiative.status)}
                        variant={statusVariant(initiative.status)}
                        size="sm"
                      />
                    ),
                  },
                  {
                    label: 'Election Cycle',
                    value: cycleLabel(initiative) || '—',
                  },
                  {
                    label: 'Jurisdiction',
                    value: initiative.jurisdiction_name
                      ? `${jurisdictionLabel(initiative.jurisdiction_level)}: ${initiative.jurisdiction_name}`
                      : jurisdictionLabel(initiative.jurisdiction_level),
                  },
                  { label: 'District', value: initiative.district_display ?? '—' },
                  {
                    label: 'SOS Filing Number',
                    value: initiative.sos_initiative_number ?? '—',
                    copyValue: initiative.sos_initiative_number ?? undefined,
                  },
                  { label: 'Unofficial Caption', value: initiative.unofficial_caption ?? '—' },
                  { label: 'Ballot Order', value: initiative.ballot_order?.toString() ?? '—' },
                  {
                    label: 'Approval Threshold',
                    value: initiative.approval_threshold_pct != null ? thresholdPct + '%' : '—',
                  },
                  {
                    // Null is 'not yet heard', not 'refused' — a local measure never goes
                    // to the Title Board at all.
                    label: 'Single Subject Approved',
                    value: initiative.is_single_subject_approved == null
                      ? 'Not determined'
                      : (initiative.is_single_subject_approved ? 'Yes' : 'No'),
                  },
                ]}
              />
            </div>

            <div className="card p-5">
              <CardHeader title="Election Results" onEdit={() => openSection('results')} />
              <FieldList
                fields={[
                  { label: 'Votes Yes', value: initiative.votes_yes?.toLocaleString() ?? '—' },
                  { label: 'Yes %', value: yesPct === null ? '—' : yesPct + '%' },
                  {
                    label: 'Outcome',
                    value: passed === null
                      ? '—'
                      : (passed ? 'Passed' : 'Failed') + ' against ' + thresholdPct + '% required',
                  },
                  { label: 'Votes No', value: initiative.votes_no?.toLocaleString() ?? '—' },
                  { label: 'Result Certified', value: initiative.result_certified ? 'Yes' : 'No' },
                  {
                    label: 'Effective Date',
                    value: initiative.effective_date ? formatDate(initiative.effective_date) : '—',
                  },
                ]}
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="card p-5">
              <CardHeader title="Election Calendar" />
              <p className="mb-2 text-sm text-text-secondary dark:text-text-dark-secondary">
                {cycleLabel(initiative) || '—'}
              </p>
              {initiative.milestones.length === 0 ? (
                <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
                  No milestones set for this election calendar.
                </p>
              ) : (
                <ul className="space-y-1.5">
                  {initiative.milestones.map((milestone) => (
                    <li key={milestone.type} className="flex items-center justify-between text-sm">
                      <span className="text-text-secondary dark:text-text-dark-secondary">{milestone.label}</span>
                      <span className="tabular-nums text-text-primary dark:text-text-dark-primary">
                        {formatDate(milestone.date)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="card p-5">
              <CardHeader title="Links & Resources" onEdit={() => openSection('links')} />
              {links.length === 0 ? (
                <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">No links added yet.</p>
              ) : (
                <ul className="space-y-1.5">
                  {links.map((link) => (
                    <li key={link.label}>
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-sm text-primary-600 hover:underline dark:text-primary-400"
                      >
                        <ExternalLink className="h-3 w-3" />
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="card p-5">
              <CardHeader title="Admin Notes" onEdit={() => openSection('notes')} />
              {initiative.notes ? (
                <RichContent
                  html={initiative.notes}
                  className="text-sm text-text-primary dark:text-text-dark-primary"
                />
              ) : (
                <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">No notes.</p>
              )}
            </div>

            {initiative.tracking.length > 0 && (
              <div className="card p-5">
                <CardHeader title={`Workspace Tracking (${initiative.tracking.length})`} />
                <ul className="space-y-1.5">
                  {initiative.tracking.map((entry) => (
                    <li key={entry.id} className="flex items-center justify-between gap-2 text-sm">
                      <span className="text-text-primary dark:text-text-dark-primary">{entry.workspace_name}</span>
                      <span className="flex items-center gap-1.5">
                        {entry.position && <Chip label={entry.position} variant="neutral" size="xs" />}
                        {entry.is_hot && <Chip label="HOT" variant="danger" size="xs" />}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="card p-5">
              <CardHeader title="Metadata" />
              <FieldList
                fields={[
                  {
                    label: 'ID',
                    value: <span className="font-mono text-xs">{initiative.id}</span>,
                    copyValue: initiative.id,
                  },
                  { label: 'SOS Measure ID', value: initiative.sos_measure_id ?? '—' },
                  { label: 'Created', value: formatDate(initiative.created_at) },
                  { label: 'Updated', value: formatDate(initiative.updated_at) },
                ]}
              />
              {/* Every column, whether or not a card renders it. The People and
                  Accounts tools carry this so a schema addition is visible on day
                  one instead of waiting for someone to notice it is missing. */}
              <AllFieldsSection record={initiative as unknown as Record<string, unknown>} />
            </div>
          </div>
        </div>
      )}

      {tab === 'lifecycle' && (
        <div className="space-y-4">
            {showPetitionCard && (
              <div className="card p-5">
                <CardHeader title="Petition Tracking" onEdit={() => openSection('petition')} />
                <FieldList
                  fields={[
                    {
                      label: 'Signatures Required',
                      value: initiative.signatures_required?.toLocaleString() ?? '—',
                    },
                    {
                      label: 'Signatures Submitted',
                      value: initiative.signatures_submitted?.toLocaleString() ?? '—',
                    },
                    {
                      label: 'Petition Deadline',
                      value: initiative.petition_deadline ? formatDate(initiative.petition_deadline) : '—',
                    },
                  ]}
                />
              </div>
            )}

            {showLifecycleCard && (
              <InitiativeLifecycleCard
                initiativeId={initiative.id}
                events={initiative.status_history ?? []}
                signaturesRequired={initiative.signatures_required}
                signaturesSubmitted={initiative.signatures_submitted}
                signaturesValid={initiative.signatures_valid}
              />
            )}

            <InitiativeProponentsCard
              initiativeId={initiative.id}
              proponents={initiative.proponents ?? []}
            />

        </div>
      )}

      {tab === 'content' && (
        <div className="space-y-4">
            <BallotAnalysisCard initiative={initiative} />
            <InitiativeCitizenContentCard
              initiativeId={initiative.id}
              status={initiative.status}
              publicationStatus={initiative.publication_status}
              onPublish={() => setPublishOpen(true)}
              twin={initiative.ballot_visible_twin}
              values={{
                plain_language_summary: initiative.plain_language_summary,
                plain_language_long_description: initiative.plain_language_long_description,
                what_yes_means: initiative.what_yes_means,
                what_no_means: initiative.what_no_means,
                who_supports_and_why: initiative.who_supports_and_why,
                who_opposes_and_why: initiative.who_opposes_and_why,
                background_context: initiative.background_context,
              }}
              provenance={initiative.content_provenance}
            />


            <InitiativeFiscalImpactCard
              initiativeId={initiative.id}
              statement={initiative.fiscal_impact_statement}
              detail={initiative.fiscal_impact_detail}
              provenance={initiative.content_provenance}
              fiscalImpactUrl={initiative.fiscal_impact_url}
            />


            <div className="card p-5">
              <CardHeader title="Final Measure Text" onEdit={() => openSection('full_text')} />
              {initiative.full_text ? (
                <p className="max-h-64 overflow-y-auto whitespace-pre-wrap text-sm text-text-primary dark:text-text-dark-primary">
                  {initiative.full_text}
                </p>
              ) : (
                <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
                  Not stored. The measure is still reachable at its Full Text URL if one is set.
                </p>
              )}
            </div>

            {initiative.blue_book_entry_status !== 'not_applicable' && (
              <InitiativeBlueBookCard
                initiativeId={initiative.id}
                sections={initiative.blue_book_sections ?? []}
                edition={initiative.blue_book_edition}
                retrievedOn={initiative.blue_book_retrieved_on}
                entryStatus={initiative.blue_book_entry_status}
                verifiedAt={initiative.blue_book_verified_at}
                bookUrl={measureBlueBookUrl(initiative)}
              />
            )}

            {/* Last on this tab: the traceability record documents the fields above it (PRD v2 §5.13.3). */}
            <InitiativeDocumentsCard initiativeId={initiative.id} />
        </div>
      )}

      {tab === 'sides' && (
        <InitiativeSidesTab initiative={initiative} onChanged={invalidate} />
      )}

      {tab === 'source-changes' && initiativeId && (
        <OfficialSourceUpdates initiativeId={initiativeId} />
      )}

      {tab === 'activity' && initiativeId && (
        <EntityAuditList entityType="ballot_initiative" entityId={initiativeId} />
      )}

      <AdminModal
        isOpen={editSection !== null}
        title={editSection ? SECTION_FORMS[editSection].title : ''}
        onClose={() => setEditSection(null)}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setEditSection(null)}>Cancel</Button>
            <Button variant="primary" size="sm" loading={updateMutation.isLoading} onClick={saveSection}>
              Save Changes
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {editSection &&
            SECTION_FORMS[editSection].fields.map((field) =>
              field.type === 'checkbox' ? (
                <label key={field.key} className="flex items-center gap-2 text-sm text-text-secondary dark:text-text-dark-secondary">
                  <input
                    type="checkbox"
                    checked={Boolean(editForm[field.key])}
                    onChange={(event) =>
                      setEditForm((current) => ({ ...current, [field.key]: event.target.checked }))
                    }
                    className="rounded"
                  />
                  {field.label}
                </label>
              ) : (
                <div key={field.key}>
                  <label htmlFor={`initiative-edit-${field.key}`} className={labelClasses}>
                    {field.label}
                  </label>
                  {field.type === 'richtext' ? (
                    <QuillEditor
                      value={String(editForm[field.key] ?? '')}
                      onChange={(value) =>
                        setEditForm((current) => ({ ...current, [field.key]: value }))
                      }
                      placeholder={field.placeholder}
                    />
                  ) : field.type === 'textarea' ? (
                    <textarea
                      id={`initiative-edit-${field.key}`}
                      rows={field.rows ?? 3}
                      value={String(editForm[field.key] ?? '')}
                      placeholder={field.placeholder}
                      onChange={(event) =>
                        setEditForm((current) => ({ ...current, [field.key]: event.target.value }))
                      }
                      className={inputClasses}
                    />
                  ) : (
                    <input
                      id={`initiative-edit-${field.key}`}
                      type={field.type}
                      value={String(editForm[field.key] ?? '')}
                      placeholder={field.placeholder}
                      onChange={(event) =>
                        setEditForm((current) => ({ ...current, [field.key]: event.target.value }))
                      }
                      className={inputClasses}
                    />
                  )}
                </div>
              )
            )}
        </div>
      </AdminModal>

      <AdminModal
        isOpen={classificationOpen}
        title="Edit Classification"
        onClose={() => setClassificationOpen(false)}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setClassificationOpen(false)}>Cancel</Button>
            <Button variant="primary" size="sm" loading={updateMutation.isLoading} onClick={saveClassification}>
              Save Changes
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="classification-type" className={labelClasses}>Measure Type</label>
              <select
                id="classification-type"
                value={classificationForm.measure_type}
                onChange={(event) =>
                  setClassificationForm((current) => ({
                    ...current,
                    measure_type: event.target.value,
                    approval_threshold_pct: String(defaultThresholdFor(event.target.value)),
                  }))
                }
                className={inputClasses}
              >
                {MEASURE_TYPES.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="classification-threshold" className={labelClasses}>Approval Threshold (%)</label>
              <input
                id="classification-threshold"
                type="number"
                min={1}
                max={100}
                step={0.1}
                value={classificationForm.approval_threshold_pct}
                onChange={(event) =>
                  setClassificationForm((current) => ({ ...current, approval_threshold_pct: event.target.value }))
                }
                className={inputClasses}
              />
              <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                Constitutional amendments need 55% under Amendment 71 (2016). Repeal-only
                amendments stay at 50 — change it back if this is one.
              </p>
            </div>

            <div>
              <label htmlFor="classification-sos-number" className={labelClasses}>SOS Filing Number</label>
              <input
                id="classification-sos-number"
                type="text"
                value={classificationForm.sos_initiative_number}
                onChange={(event) =>
                  setClassificationForm((current) => ({ ...current, sos_initiative_number: event.target.value }))
                }
                className={inputClasses}
                placeholder="e.g. 2025-2026 #85"
              />
              <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                The filing number, not the ballot number — a measure carries this for months
                before it is assigned a Proposition or Amendment letter.
              </p>
            </div>

            <div>
              <label htmlFor="classification-caption" className={labelClasses}>Unofficial Caption</label>
              <input
                id="classification-caption"
                type="text"
                value={classificationForm.unofficial_caption}
                onChange={(event) =>
                  setClassificationForm((current) => ({ ...current, unofficial_caption: event.target.value }))
                }
                className={inputClasses}
                placeholder="Legislative staff caption, used before a title is set"
              />
            </div>

            <div>
              <label htmlFor="classification-ballot-order" className={labelClasses}>Ballot Order</label>
              <input
                id="classification-ballot-order"
                type="number"
                min={1}
                value={classificationForm.ballot_order}
                onChange={(event) =>
                  setClassificationForm((current) => ({ ...current, ballot_order: event.target.value }))
                }
                className={inputClasses}
              />
              <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                Position on the printed ballot. Left empty, measures order by number as before.
              </p>
            </div>

            <div>
              <label htmlFor="classification-single-subject" className={labelClasses}>Single Subject Approved</label>
              <select
                id="classification-single-subject"
                value={classificationForm.is_single_subject_approved}
                onChange={(event) =>
                  setClassificationForm((current) => ({ ...current, is_single_subject_approved: event.target.value }))
                }
                className={inputClasses}
              >
                <option value="">Not determined</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
              <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                The Title Board determination. Local measures never go before the Board, so
                theirs stays undetermined.
              </p>
            </div>
            <div>
              <label htmlFor="classification-source" className={labelClasses}>Initiative Source</label>
              <select
                id="classification-source"
                value={classificationForm.initiative_source}
                onChange={(event) =>
                  setClassificationForm((current) => ({ ...current, initiative_source: event.target.value }))
                }
                className={inputClasses}
              >
                {INITIATIVE_SOURCES.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="classification-cycle" className={labelClasses}>Election Cycle</label>
              <select
                id="classification-cycle"
                value={classificationForm.election_calendar_id}
                onChange={(event) =>
                  setClassificationForm((current) => ({ ...current, election_calendar_id: event.target.value }))
                }
                className={inputClasses}
              >
                {(cycles.data ?? []).map((cycle) => (
                  <option key={cycle.id} value={cycle.id}>
                    {cycle.name || `${cycle.election_year} ${cycle.state} ${cycle.election_type}`}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="classification-status" className={labelClasses}>Status</label>
              <select
                id="classification-status"
                value={classificationForm.status}
                onChange={(event) =>
                  setClassificationForm((current) => ({ ...current, status: event.target.value }))
                }
                className={inputClasses}
              >
                {INITIATIVE_STATUSES.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="classification-jurisdiction" className={labelClasses}>Jurisdiction Level</label>
              <select
                id="classification-jurisdiction"
                value={classificationForm.jurisdiction_level}
                onChange={(event) =>
                  setClassificationForm((current) => ({
                    ...current,
                    jurisdiction_level: event.target.value,
                    jurisdiction_name: '',
                    electoral_district_id: '',
                  }))
                }
                className={inputClasses}
              >
                {JURISDICTION_LEVELS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            {showDistrictSelect && (
              <div>
                <label htmlFor="classification-district" className={labelClasses}>
                  {classificationForm.jurisdiction_level === 'county' ? 'County' : 'District'}
                </label>
                <select
                  id="classification-district"
                  value={classificationForm.electoral_district_id}
                  onChange={(event) => {
                    const district = scopedDistricts.find((entry) => entry.id === event.target.value);
                    setClassificationForm((current) => ({
                      ...current,
                      electoral_district_id: event.target.value,
                      jurisdiction_name: district?.display_label ?? '',
                    }));
                  }}
                  className={inputClasses}
                >
                  <option value="">
                    Select {classificationForm.jurisdiction_level === 'county' ? 'county' : 'district'}…
                  </option>
                  {scopedDistricts.map((district) => (
                    <option key={district.id} value={district.id}>{district.display_label}</option>
                  ))}
                </select>
              </div>
            )}
            {showJurisdictionName && (
              <div>
                <label htmlFor="classification-jurisdiction-name" className={labelClasses}>Jurisdiction Name</label>
                <input
                  id="classification-jurisdiction-name"
                  type="text"
                  value={classificationForm.jurisdiction_name}
                  placeholder="e.g. City of Boulder"
                  onChange={(event) =>
                    setClassificationForm((current) => ({ ...current, jurisdiction_name: event.target.value }))
                  }
                  className={inputClasses}
                />
              </div>
            )}
          </div>
        </div>
      </AdminModal>

      <ConfirmationDialog
        isOpen={withdrawOpen}
        title="Withdraw Initiative"
        message={`Mark "${initiative.popular_name || initiative.ballot_title
          || initiative.unofficial_caption || initiative.sos_initiative_number
          || initiative.measure_number || 'Untitled measure'}" as withdrawn? This can be undone by editing the status.`}
        confirmText="Withdraw"
        cancelText="Cancel"
        variant="warning"
        onConfirm={() => withdrawMutation.mutate()}
        onCancel={() => setWithdrawOpen(false)}
      />

      <ConfirmationDialog
        isOpen={publishOpen}
        title="Publish Initiative"
        message={isBallotVisible(initiative.status)
          ? 'This measure will appear on the public measure page, the public ballot feeds, and every citizen\'s ballot within a minute. Pro users already see it.'
          : `Its status is ${statusLabel(initiative.status)}, so it stays out of public view until the status becomes qualified or on the ballot — then it appears automatically, without another publish.`}
        confirmText="Publish"
        cancelText="Cancel"
        variant="info"
        onConfirm={() => publishMutation.mutate()}
        onCancel={() => setPublishOpen(false)}
      />

      <ConfirmationDialog
        isOpen={unpublishOpen}
        title="Unpublish Initiative"
        message="This removes the measure from the public measure page, the public ballot feeds, and citizens' ballots and directory. Pro users keep seeing it. Its lifecycle status is not changed."
        confirmText="Unpublish"
        cancelText="Cancel"
        variant="warning"
        onConfirm={() => unpublishMutation.mutate()}
        onCancel={() => setUnpublishOpen(false)}
      />
    </AdminPageShell>
  );
};

export default InitiativeDetailPage;
